import React, { useState, useEffect } from 'react';
import { FiArrowLeft, FiRefreshCw, FiCornerUpLeft, FiAlertCircle, FiTrash2, FiCheck } from 'react-icons/fi';
import { getThread, markAsSpam, removeFromSpam, deleteMessages, type EmailThreadDetail, type EmailMessage } from '../api/emailApi';
import { toast } from 'react-toastify';
import { useStorageReadOnly } from '../hooks/useStorageReadOnly';

interface EmailReaderProps {
  threadId: string;
  onBack: () => void;
  onReply?: (replyData: {
    to?: string | string[];
    cc?: string | string[];
    subject?: string;
    bodyHtml?: string;
    threadId?: string;
  }) => void;
  onMarkedAsSpam?: () => void;
  onDeleted?: () => void;
}

export default function EmailReader({ threadId, onBack, onReply, onMarkedAsSpam, onDeleted }: EmailReaderProps) {
  const [thread, setThread] = useState<EmailThreadDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const { isReadOnly } = useStorageReadOnly();
  
  // Check if thread contains spam messages
  const isSpam = thread?.messages.some(msg => msg.labelIds?.includes('SPAM')) || false;

  useEffect(() => {
    loadThread();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  const loadThread = async () => {
    setLoading(true);
    try {
      const result = await getThread(threadId);
      if (result?.thread) {
        setThread(result.thread);
      }
    } catch (err) {
      console.error('[EmailReader] Failed to load thread:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('he-IL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleMarkAsSpam = async () => {
    if (!thread?.messages || processing) return;
    
    if (isReadOnly) {
      toast.error('האחסון מלא - לא ניתן לערוך או לשנות הודעות. אנא פנה למנהל המערכת כדי לפנות מקום באחסון.');
      return;
    }
    
    setProcessing(true);
    try {
      const messageIds = thread.messages.map(msg => msg.id);
      await markAsSpam(messageIds);
      toast.success('ההודעה סומנה כספאם');
      onMarkedAsSpam?.();
      // Reload thread to reflect changes
      await loadThread();
    } catch (err: any) {
      console.error('[EmailReader] Failed to mark as spam:', err);
      const errorMessage = err?.response?.data?.error || err?.message || 'שגיאה לא ידועה';
      toast.error('שגיאה בסימון כספאם: ' + errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveFromSpam = async () => {
    if (!thread?.messages || processing) return;
    
    if (isReadOnly) {
      toast.error('האחסון מלא - לא ניתן לערוך או לשנות הודעות. אנא פנה למנהל המערכת כדי לפנות מקום באחסון.');
      return;
    }
    
    setProcessing(true);
    try {
      const messageIds = thread.messages.map(msg => msg.id);
      await removeFromSpam(messageIds);
      toast.success('ההודעה הוסרה מספאם');
      onMarkedAsSpam?.();
      // Reload thread to reflect changes
      await loadThread();
    } catch (err: any) {
      console.error('[EmailReader] Failed to remove from spam:', err);
      const errorMessage = err?.response?.data?.error || err?.message || 'שגיאה לא ידועה';
      toast.error('שגיאה בהסרה מספאם: ' + errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!thread?.messages || processing) return;
    
    if (isReadOnly) {
      toast.error('האחסון מלא - לא ניתן למחוק הודעות. אנא פנה למנהל המערכת כדי לפנות מקום באחסון.');
      return;
    }
    
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את ההודעה?')) {
      return;
    }
    
    setProcessing(true);
    try {
      const messageIds = thread.messages.map(msg => msg.id);
      await deleteMessages(messageIds);
      toast.success('ההודעה נמחקה');
      onDeleted?.();
      onBack();
    } catch (err: any) {
      console.error('[EmailReader] Failed to delete:', err);
      const errorMessage = err?.response?.data?.error || err?.message || 'שגיאה לא ידועה';
      toast.error('שגיאה במחיקת ההודעה: ' + errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-slate-500">טוען...</div>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center text-slate-500">
          <p>לא ניתן לטעון את ההודעה</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Thread Header */}
      <div className="p-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={onBack}
            className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
            aria-label="חזור לרשימה"
          >
            <FiArrowLeft className="text-slate-600" />
          </button>
          <h3 className="text-lg font-bold text-slate-900 flex-1">
            {thread.subject || '(ללא נושא)'}
          </h3>
          <div className="flex items-center gap-2">
            {onReply && !isSpam && (
              <button
                onClick={() => {
                  if (isReadOnly) {
                    toast.error('האחסון מלא - לא ניתן לכתוב או לערוך מיילים. אנא פנה למנהל המערכת כדי לפנות מקום באחסון.');
                    return;
                  }
                  
                  // Get the last message to reply to
                  const lastMessage = thread.messages[thread.messages.length - 1];
                  if (lastMessage) {
                    // Extract reply-to or from address
                    const replyTo = lastMessage.from;
                    const replySubject = lastMessage.subject?.startsWith('Re:') 
                      ? lastMessage.subject 
                      : `Re: ${lastMessage.subject || ''}`;
                    
                    // Build reply body with quoted original message
                    const originalText = lastMessage.bodyText || '';
                    const quotedText = `\n\n--- Original Message ---\nFrom: ${lastMessage.from}\nDate: ${formatDate(lastMessage.sentAt)}\n\n${originalText}`;
                    const replyBodyHtml = `<br><br><hr><p><strong>--- Original Message ---</strong><br>From: ${lastMessage.from}<br>Date: ${formatDate(lastMessage.sentAt)}<br><br>${lastMessage.bodyHtml || originalText.replace(/\n/g, '<br>')}</p>`;
                    
                    onReply({
                      to: replyTo,
                      subject: replySubject,
                      bodyHtml: replyBodyHtml,
                      threadId: threadId,
                    });
                  }
                }}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                  isReadOnly
                    ? 'bg-slate-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
                disabled={processing || isReadOnly}
                title={isReadOnly ? 'האחסון מלא - לא ניתן לכתוב או לערוך מיילים' : ''}
              >
                <FiCornerUpLeft />
                השב
              </button>
            )}
            {!isSpam ? (
              <button
                onClick={handleMarkAsSpam}
                disabled={processing || isReadOnly}
                className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                  isReadOnly
                    ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                } disabled:opacity-50`}
                title={isReadOnly ? 'האחסון מלא - לא ניתן לערוך הודעות' : 'סמן כספאם'}
              >
                <FiAlertCircle />
                ספאם
              </button>
            ) : (
              <button
                onClick={handleRemoveFromSpam}
                disabled={processing || isReadOnly}
                className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                  isReadOnly
                    ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                } disabled:opacity-50`}
                title={isReadOnly ? 'האחסון מלא - לא ניתן לערוך הודעות' : 'הסר מספאם'}
              >
                <FiCheck />
                לא ספאם
              </button>
            )}
            <button
              onClick={handleDelete}
              disabled={processing || isReadOnly}
              className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                isReadOnly
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              } disabled:opacity-50`}
              title={isReadOnly ? 'האחסון מלא - לא ניתן למחוק הודעות' : 'מחק'}
            >
              <FiTrash2 />
              מחק
            </button>
            <button
              onClick={loadThread}
              disabled={processing}
              className="p-2 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
              aria-label="רענן"
            >
              <FiRefreshCw className={`text-slate-600 ${processing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {thread.messages.map((message: EmailMessage) => (
          <div
            key={message.id}
            className={`
              p-4 rounded-lg border
              ${message.direction === 'outgoing' 
                ? 'bg-blue-50 border-blue-200 ml-auto max-w-[80%]' 
                : 'bg-white border-slate-200 mr-auto max-w-[80%]'
              }
            `}
          >
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="font-semibold text-slate-900">
                  {message.direction === 'outgoing' ? 'אתה' : message.from}
                </p>
                <p className="text-xs text-slate-500">
                  {message.direction === 'outgoing' 
                    ? `אל: ${message.to.join(', ')}`
                    : `אל: ${message.to.join(', ')}`
                  }
                </p>
              </div>
              <p className="text-xs text-slate-400">
                {formatDate(message.sentAt)}
              </p>
            </div>
            
            <div className="mt-3">
              {message.bodyHtml ? (
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: message.bodyHtml }}
                />
              ) : (
                <p className="text-slate-700 whitespace-pre-wrap">
                  {message.bodyText || '(אין תוכן)'}
                </p>
              )}
            </div>

            {message.attachments && message.attachments.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                <p className="text-xs text-slate-500 mb-2">קבצים מצורפים:</p>
                <div className="space-y-1">
                  {message.attachments.map((att, idx) => (
                    <div key={idx} className="text-xs text-slate-600">
                      📎 {att.filename} ({Math.round(att.size / 1024)} KB)
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


